import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store, { getLayerOpacity } from '../state';
import { getTileUrl } from '../config';
import { FoliageService } from '../services/FoliageService';

const ALL_SPECIES: Array<'maple' | 'aspen' | 'oak' | 'larch'> = ['maple', 'aspen', 'oak', 'larch'];

export class FoliageLayer extends BaseProduct {
  public readonly id: ProductId = 'foliage';
  private unsubscribeStore: (() => void) | null = null;
  private lastDate: string = '';
  private lastViewMode: string = '';
  private lastSpeciesKey: string = '';
  private lastTreedOnly: boolean = false;

  protected onActivate(): void {
    this.refresh();
    this.unsubscribeStore = store.subscribe((s) => {
      if (!this.active) return;
      const defaultDate = FoliageService.getDefaultDateInfo().dateStr;
      const currentDate = s.foliageSelectedDate || defaultDate;
      const currentMode = s.foliageViewMode || 'peak_date';
      const currentSpecies = (s.foliageSpeciesSelected || ALL_SPECIES).slice().sort().join(',');
      const currentTreedOnly = !!s.foliageTreedOnly;
      if (
        currentDate !== this.lastDate ||
        currentMode !== this.lastViewMode ||
        currentSpecies !== this.lastSpeciesKey ||
        currentTreedOnly !== this.lastTreedOnly
      ) {
        this.refresh();
      }
    });
  }

  protected onDeactivate(): void {
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
    this.layerManager.removeBufferedRasterLayer(this.id);
    this.layerManager.removeLayer(this.id);
    this.layerManager.removeLayer('foliage_canopy');
    ALL_SPECIES.forEach(sp => {
      this.layerManager.removeLayer(`foliage_species_${sp}`);
    });
    this.lastDate = '';
    this.lastViewMode = '';
    this.lastSpeciesKey = '';
    this.lastTreedOnly = false;
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public refresh(): void {
    if (!this.active) return;
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return;

    const opacity = getLayerOpacity(this.id, 1.0);
    const defaultDate = FoliageService.getDefaultDateInfo().dateStr;
    const dateStr = state.foliageSelectedDate || defaultDate;
    const viewMode = state.foliageViewMode || 'peak_date';
    const selectedSpecies = state.foliageSpeciesSelected || ALL_SPECIES;
    const speciesKey = selectedSpecies.slice().sort().join(',');
    const treedOnly = !!state.foliageTreedOnly;

    this.lastDate = dateStr;
    this.lastViewMode = viewMode;
    this.lastSpeciesKey = speciesKey;
    this.lastTreedOnly = treedOnly;

    if (viewMode === 'species') {
      // Clean up single raster forecast/canopy layers
      this.layerManager.removeBufferedRasterLayer(this.id);
      this.layerManager.removeLayer(this.id);
      this.layerManager.removeLayer('foliage_canopy');

      // Update or remove each species layer based on selection
      ALL_SPECIES.forEach(sp => {
        const layerId = `foliage_species_${sp}`;
        if (selectedSpecies.includes(sp)) {
          const tiles = [
            getTileUrl(manifest, layerId as ProductId, 'seasonal', dateStr, '')
          ];
          this.layerManager.updateRasterLayer(layerId, tiles, opacity, undefined, 0, 6, 'nearest');
        } else {
          this.layerManager.removeLayer(layerId);
        }
      });
    } else {
      // Remove all species layers when not in species mode
      ALL_SPECIES.forEach(sp => {
        this.layerManager.removeLayer(`foliage_species_${sp}`);
      });

      let targetProduct: ProductId = treedOnly ? 'foliage_treed' : 'foliage';
      if (viewMode === 'vibrancy') {
        targetProduct = treedOnly ? 'foliage_vibrancy_treed' : 'foliage_vibrancy';
      } else if (viewMode === 'peak_date') {
        targetProduct = treedOnly ? 'foliage_peak_date_treed' : 'foliage_peak_date';
      } else if (viewMode === 'canopy') {
        targetProduct = 'foliage_canopy';
      } else if (viewMode === 'heat_stress') {
        targetProduct = 'foliage_summer_temp_anomaly';
      } else if (viewMode === 'soil_moisture') {
        targetProduct = 'foliage_soil_moisture_anomaly';
      }

      const tiles = [
        getTileUrl(manifest, targetProduct, 'seasonal', dateStr, '')
      ];

      this.layerManager.updateBufferedRasterLayer(this.id, tiles, opacity, undefined, 0, 6, 'nearest');
    }
  }
}

export default FoliageLayer;
